import React, { useState } from "react";
import {
  AlertCircle,
  Check,
  Copy,
  Lock,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import ArtifactBlock from "@/components/ArtifactBlock";
import MessageAttachments from "@/components/MessageAttachments";

const SENDER_STYLES = {
  Arthur: {
    bg: "#D96C4A",
    text: "#FFFFFF",
    labelColor: "rgba(255,255,255,0.85)",
    timestampColor: "rgba(255,255,255,0.7)",
    privateBorder: "#A14225",
    mentionTone: "#8C3A1F",
  },
  Simon: {
    bg: "#3A5A40",
    text: "#FFFFFF",
    labelColor: "rgba(255,255,255,0.85)",
    timestampColor: "rgba(255,255,255,0.7)",
    privateBorder: "#274030",
    mentionTone: "#274030",
  },
};

const DEFAULT_STYLE = {
  bg: "#2C2A29",
  text: "#FFFFFF",
  labelColor: "rgba(255,255,255,0.85)",
  timestampColor: "rgba(255,255,255,0.7)",
  privateBorder: "#7A4A2C",
  mentionTone: "#7A4A2C",
};

const MENTION_RE = /@([A-Za-z][A-Za-z0-9_]*)/g;
const CLAUDE_BG =
  "linear-gradient(135deg, #F7F3EC 0%, #ECE6DA 60%, #E2D9C7 100%)";

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function MentionPill({ name }) {
  const palette = SENDER_STYLES[name] || DEFAULT_STYLE;
  const tone = palette.mentionTone;
  return (
    <span
      className="inline-flex items-center px-1.5 py-px rounded-md font-medium align-baseline mx-px"
      style={{
        background: `${tone}1F`,
        color: tone,
        border: `1px solid ${tone}44`,
      }}
      data-testid={`mention-pill-${name}`}
    >
      @{name}
    </span>
  );
}

function renderContentParts(content) {
  if (!content) return null;
  const parts = [];
  MENTION_RE.lastIndex = 0;
  let lastIndex = 0;
  let m;
  while ((m = MENTION_RE.exec(content)) !== null) {
    if (m.index > lastIndex) {
      parts.push(content.slice(lastIndex, m.index));
    }
    parts.push(<MentionPill key={`m-${m.index}`} name={m[1]} />);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }
  return parts;
}

/* ─────────── Phase 6a: markdown rendering for Claude ─────────── */

function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    let ok = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        ok = true;
      } catch {
        /* swallow */
      }
    }
    // Toggle the label either way — the user did click Copy and either path
    // got them as close as we can get them. Worst case the clipboard is
    // empty; the visual confirmation is more important than perfection in
    // insecure contexts.
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-sans font-medium text-[#2C2A29] hover:bg-[#E8E2D2] transition-colors"
    >
      {copied ? (
        <>
          <Check size={10} strokeWidth={2} /> Copied
        </>
      ) : (
        <>
          <Copy size={10} strokeWidth={2} /> Copy
        </>
      )}
    </button>
  );
}

function CodeBlock({ language, value, messageId }) {
  const safeValue = typeof value === "string" ? value : String(value || "");
  return (
    <div
      className="not-prose my-3 rounded-lg overflow-hidden border border-[#DCD7CE] bg-[#FBF8F2]"
      data-testid={`markdown-code-block-${messageId}`}
    >
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#DCD7CE] bg-[#F3EFE5]">
        <span className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold">
          {language || "code"}
        </span>
        <CopyButton
          value={safeValue}
          testid={`markdown-code-copy-${messageId}`}
        />
      </div>
      <div className="overflow-x-auto">
        <SyntaxHighlighter
          language={language || "text"}
          style={oneLight}
          customStyle={{
            margin: 0,
            padding: "0.75rem 1rem",
            background: "transparent",
            fontSize: "12.5px",
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            lineHeight: 1.55,
          }}
          codeTagProps={{
            style: {
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            },
          }}
          PreTag="div"
          wrapLongLines={false}
        >
          {safeValue}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}

function makeMarkdownComponents(messageId) {
  return {
    h1: ({ node, ...p }) => (
      <h1
        className="font-serif text-lg font-semibold mt-3 mb-1.5 leading-tight text-[#2C2A29]"
        {...p}
      />
    ),
    h2: ({ node, ...p }) => (
      <h2
        className="font-serif text-base font-semibold mt-3 mb-1.5 leading-tight text-[#2C2A29]"
        {...p}
      />
    ),
    h3: ({ node, ...p }) => (
      <h3
        className="font-serif text-base font-semibold mt-2.5 mb-1 leading-tight text-[#2C2A29]"
        {...p}
      />
    ),
    h4: ({ node, ...p }) => (
      <h4
        className="font-sans text-sm font-semibold mt-2.5 mb-1 text-[#2C2A29]"
        {...p}
      />
    ),
    h5: ({ node, ...p }) => (
      <h5
        className="font-sans text-sm font-semibold mt-2 mb-1 text-[#2C2A29]"
        {...p}
      />
    ),
    h6: ({ node, ...p }) => (
      <h6
        className="font-sans text-xs font-semibold uppercase tracking-wider mt-2 mb-1 text-[#7A4A2C]"
        {...p}
      />
    ),
    p: ({ node, ...p }) => (
      <p className="mb-2 last:mb-0 leading-relaxed" {...p} />
    ),
    ul: ({ node, ordered, ...p }) => (
      <ul
        className="list-disc pl-5 mb-2 last:mb-0 space-y-0.5 marker:text-[#7A4A2C]"
        {...p}
      />
    ),
    ol: ({ node, ordered, ...p }) => (
      <ol
        className="list-decimal pl-5 mb-2 last:mb-0 space-y-0.5 marker:text-[#7A4A2C]"
        {...p}
      />
    ),
    li: ({ node, ordered, checked, className, ...p }) => {
      if (checked !== null && checked !== undefined) {
        return (
          <li className="leading-relaxed list-none -ml-5 flex items-start gap-1.5">
            <input
              type="checkbox"
              disabled
              checked={!!checked}
              readOnly
              className="mt-1 accent-[#D96C4A]"
            />
            <span {...p} />
          </li>
        );
      }
      return <li className="leading-relaxed" {...p} />;
    },
    blockquote: ({ node, ...p }) => (
      <blockquote
        className="border-l-2 border-[#D96C4A]/60 pl-3 my-2 italic text-[#5a5754]"
        {...p}
      />
    ),
    hr: () => <hr className="my-3 border-t border-[#DCD7CE]" />,
    a: ({ node, ...p }) => (
      <a
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 decoration-[#D96C4A]/50 hover:text-[#D96C4A] hover:decoration-[#D96C4A] transition-colors break-words"
        {...p}
      />
    ),
    img: ({ node, alt, ...p }) => (
      <img
        alt={alt || ""}
        className="max-w-md w-full rounded-lg my-2 border border-[#DCD7CE]"
        {...p}
      />
    ),
    strong: ({ node, ...p }) => (
      <strong className="font-semibold text-[#2C2A29]" {...p} />
    ),
    em: ({ node, ...p }) => <em className="italic" {...p} />,
    del: ({ node, ...p }) => (
      <del className="line-through opacity-70" {...p} />
    ),
    table: ({ node, ...p }) => (
      <div className="not-prose overflow-x-auto my-3 rounded-lg border border-[#DCD7CE]">
        <table className="border-collapse text-sm w-full" {...p} />
      </div>
    ),
    thead: ({ node, ...p }) => <thead className="bg-[#F3EFE5]" {...p} />,
    tbody: ({ node, ...p }) => <tbody {...p} />,
    tr: ({ node, ...p }) => (
      <tr className="border-b border-[#DCD7CE] last:border-b-0" {...p} />
    ),
    th: ({ node, ...p }) => (
      <th
        className="border-r border-[#DCD7CE] last:border-r-0 px-2.5 py-1.5 text-left font-semibold text-[#2C2A29] text-xs uppercase tracking-wider"
        {...p}
      />
    ),
    td: ({ node, ...p }) => (
      <td
        className="border-r border-[#DCD7CE] last:border-r-0 px-2.5 py-1.5 align-top"
        {...p}
      />
    ),
    // Unwrap <pre> — our CodeBlock has its own container.
    pre: ({ node, children }) => <>{children}</>,
    code: ({ node, inline, className, children, ...rest }) => {
      const match = /language-([\w-]+)/.exec(className || "");
      const text = Array.isArray(children)
        ? children.join("")
        : String(children ?? "");
      // react-markdown 9 doesn't always pass `inline`. Treat as block when
      // it has a language hint OR a newline.
      const looksBlock = !!match || /\n/.test(text);
      if (!looksBlock) {
        return (
          <code
            className="font-mono text-[0.9em] bg-[#EFEAE0]/70 text-[#2C2A29] px-1.5 py-0.5 rounded"
            {...rest}
          >
            {children}
          </code>
        );
      }
      const lang = match ? match[1].toLowerCase() : "";
      const cleanCode = text.replace(/\n$/, "");
      // Phase 7a — html/svg blocks get the interactive ArtifactBlock with
      // a sandboxed preview tab. Everything else keeps the existing
      // syntax-highlighted CodeBlock + Copy treatment.
      if (lang === "html" || lang === "svg") {
        return (
          <ArtifactBlock
            language={lang}
            code={cleanCode}
            messageId={messageId}
          />
        );
      }
      return (
        <CodeBlock
          language={lang}
          value={cleanCode}
          messageId={messageId}
        />
      );
    },
  };
}

const REMARK_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [rehypeSanitize];

function ClaudeMarkdown({ text, messageId }) {
  return (
    <div
      className="markdown-body font-sans text-[15px] leading-relaxed break-words"
      data-testid={`message-content-${messageId}`}
    >
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={makeMarkdownComponents(messageId)}
      >
        {text || ""}
      </ReactMarkdown>
    </div>
  );
}

/* ─────────── Phase 4: thinking placeholder ─────────── */
function ThinkingBubble({ message }) {
  return (
    <div
      className="flex w-full justify-start mb-6"
      data-testid={`message-row-${message.id}`}
      data-thinking="true"
    >
      <div
        className="rounded-2xl rounded-bl-md px-5 py-3.5 shadow-sm flex items-center gap-3"
        style={{
          background: CLAUDE_BG,
          color: "#2C2A29",
          border: "1px solid #DCD7CE",
        }}
        data-testid={`message-bubble-${message.id}`}
      >
        <Sparkles
          size={11}
          strokeWidth={2}
          style={{ color: "#7A4A2C" }}
          aria-hidden="true"
        />
        <span
          className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: "#7A4A2C" }}
        >
          Claude is thinking
        </span>
        <span
          className="flex gap-1 items-center"
          aria-hidden="true"
          data-testid={`thinking-dots-${message.id}`}
        >
          <span
            className="thinking-dot"
            style={{ background: "#7A4A2C", animationDelay: "0ms" }}
          />
          <span
            className="thinking-dot"
            style={{ background: "#7A4A2C", animationDelay: "200ms" }}
          />
          <span
            className="thinking-dot"
            style={{ background: "#7A4A2C", animationDelay: "400ms" }}
          />
        </span>
      </div>
    </div>
  );
}

export default function MessageBubble({
  message,
  currentUser,
  onRetry,
  showSender = true,
  showTimestamp = true,
}) {
  // Thinking placeholder is its own dedicated render — short-circuit.
  if (message.thinking) {
    return <ThinkingBubble message={message} />;
  }

  const isAssistant = message.sender_type === "assistant";
  const isMine =
    !isAssistant &&
    currentUser &&
    message.sender_email &&
    message.sender_email.toLowerCase() === currentUser.email.toLowerCase();
  const isPrivate = !!message.excluded_from_ai;
  const isUserError = !!(message.error && message.sender_type === "user");
  const isAssistantError = !!(
    message.error && message.sender_type === "assistant"
  );

  const alignRight = isMine;
  const senderName =
    message.sender_name || (isAssistant ? "Claude" : "Unknown");

  let bubbleStyle = {};
  let labelStyle = {};
  let timestampStyle = {};
  let footerStyle = {};
  let extraClasses = "";

  if (isAssistant) {
    if (isAssistantError) {
      bubbleStyle = {
        background: "#FAF2F0",
        color: "#2C2A29",
        border: "1.5px dashed #8C3A1F",
      };
      labelStyle = { color: "#8C3A1F" };
      timestampStyle = { color: "#7A7571" };
    } else {
      bubbleStyle = {
        background: CLAUDE_BG,
        color: "#2C2A29",
        border: "1px solid #DCD7CE",
      };
      labelStyle = { color: "#7A4A2C" };
      timestampStyle = { color: "#7A7571" };
    }
  } else {
    const palette = SENDER_STYLES[senderName] || DEFAULT_STYLE;
    if (isPrivate) {
      bubbleStyle = {
        background: "#FAF5EA",
        color: "#2C2A29",
        border: `1.5px dashed ${palette.privateBorder}`,
      };
      labelStyle = { color: palette.privateBorder };
      timestampStyle = { color: "#7A7571" };
      footerStyle = { color: palette.privateBorder };
    } else {
      bubbleStyle = { background: palette.bg, color: palette.text };
      labelStyle = { color: palette.labelColor };
      timestampStyle = { color: palette.timestampColor };
    }
  }

  if (isMine && !isPrivate && !isAssistant && !isUserError) {
    extraClasses = "ring-2 ring-[#2C2A29]/10";
  }
  if (message.pending) {
    bubbleStyle = { ...bubbleStyle, opacity: 0.7 };
  }
  if (isUserError) {
    bubbleStyle = { ...bubbleStyle, opacity: 1, border: `1.5px solid #8C3A1F` };
  }

  const wrapperClass = alignRight
    ? `flex w-full justify-end ${showTimestamp ? "mb-6" : "mb-1.5"}`
    : `flex w-full justify-start ${showTimestamp ? "mb-6" : "mb-1.5"}`;
  const shapeClass = alignRight ? "rounded-br-md" : "rounded-bl-md";

  // For assistant bubbles we let markdown's own code blocks/tables expand,
  // so loosen the max-width a touch.
  const widthClasses =
    isAssistant && !isAssistantError
      ? "max-w-[88%] sm:max-w-[80%]"
      : "max-w-[78%] sm:max-w-[70%]";

  return (
    <div
      className={wrapperClass}
      data-testid={`message-row-${message.id}`}
      data-sender={senderName}
      data-sender-type={message.sender_type}
      data-private={isPrivate ? "true" : "false"}
      data-pending={message.pending ? "true" : "false"}
      data-error={message.error ? "true" : "false"}
    >
      <div
        style={bubbleStyle}
        className={`${widthClasses} rounded-2xl ${shapeClass} px-5 py-3 shadow-sm flex flex-col ${extraClasses}`}
        data-testid={`message-bubble-${message.id}`}
      >
        {showSender ? (
          <div className="flex items-center gap-1.5 mb-1.5">
            {isAssistant && !isAssistantError ? (
              <Sparkles
                size={11}
                strokeWidth={2}
                style={{ color: "#7A4A2C" }}
                aria-hidden="true"
              />
            ) : null}
            {isAssistantError ? (
              <AlertCircle
                size={11}
                strokeWidth={2.2}
                style={{ color: "#8C3A1F" }}
                aria-hidden="true"
              />
            ) : null}
            {isPrivate ? (
              <Lock
                size={11}
                strokeWidth={2.2}
                style={{ color: labelStyle.color }}
                aria-hidden="true"
                data-testid={`message-lock-${message.id}`}
              />
            ) : null}
            <span
              className="font-sans text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={labelStyle}
            >
              {senderName}
              {isMine ? " · you" : ""}
            </span>
          </div>
        ) : null}

        {/* Content: Claude → markdown; humans → plain text + @-mention pills. */}
        {isAssistant && !isAssistantError ? (
          <ClaudeMarkdown text={message.content} messageId={message.id} />
        ) : (
          <div
            className="font-sans text-[15px] leading-relaxed whitespace-pre-wrap break-words"
            data-testid={`message-content-${message.id}`}
          >
            {renderContentParts(message.content)}
          </div>
        )}

        {/* Phase 7c — image attachments (user messages render them; assistant
            messages don't carry attachments today but will work the same way
            if/when they do). */}
        <MessageAttachments
          attachments={message.attachments}
          messageId={message.id}
          alignment={isMine ? "right" : "left"}
        />

        {isPrivate ? (
          <div
            className="font-sans text-[10px] mt-2 italic"
            style={footerStyle}
            data-testid={`message-private-label-${message.id}`}
          >
            private — Claude can&apos;t see this
          </div>
        ) : null}
        {isUserError ? (
          <button
            type="button"
            onClick={() => onRetry && onRetry(message)}
            data-testid={`retry-button-${message.id}`}
            className="mt-2 flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#8C3A1F] hover:bg-[#7A3019] text-white text-[11px] font-sans font-medium self-end transition-colors"
          >
            <RotateCcw size={10} strokeWidth={2.2} />
            <span>Failed — retry</span>
          </button>
        ) : null}
        {showTimestamp ? (
          <div
            className="font-sans text-[10px] mt-1 self-end"
            style={timestampStyle}
            title={
              message.created_at
                ? new Date(message.created_at).toLocaleString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : ""
            }
          >
            {formatTime(message.created_at)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
